'use client';

import { Icon, Tooltip } from '~/components/primitives';
import Link from 'next/link';
import { usePortalCtx } from '~/features/global/portal';
import { Auth } from '~/modules/auth/client';
import { useI18n } from '~/features/i18n';
import { I18N_CONF } from '@funkey/locales/config';
import { cn } from '~/utils';
import { ImageApi_createURL } from '~/app/api/image/[...identifier]/shared';
import Avatar from './Avatar';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '~/modules/trpc/client';

const AccountButton: React.FC<
    {
        /** @default false */
        full?: boolean;
    } & Pick<React.ComponentProps<typeof Avatar>, 'color'>
> = props => {
    const portal = usePortalCtx();
    const auth = Auth.useSession();
    const i18n = useI18n();
    const trpc = useTRPC();

    const [firstLetter] = useMemo(
        () => auth.user?.name || auth.user?.firstName || auth.user?.email || '',
        [auth.user],
    );

    const tenants = useQuery(
        trpc.auth.tenants.queryOptions(void undefined, {
            enabled: portal.portal === 'WEB',
        }),
    );

    const href = useMemo<string>(() => {
        if (portal.portal === 'BIZZ') {
            if (auth.user) return `${portal.baseURLPath}/settings/profile`;
            else return `/${i18n.locale}/bizz/login`;
        }

        if (auth.user?.isSuperAdmin) return `/${i18n.locale}/admin`;
        if (tenants.data?.find(t => t.type === 'BIZZ')) return `/${i18n.locale}/bizz`;
        if (tenants.data?.find(t => t.type === 'PARTNER')) return `/${i18n.locale}/partner`;

        return I18N_CONF.getTranslatedUrl('login', { locale: i18n.locale });
    }, [portal.portal, portal.baseURLPath, i18n.locale, auth.user, tenants.data]);

    return auth.user ? (
        <Tooltip
            content={`${auth.user.firstName || ''} ${auth.user.lastName || ''} - ${auth.user.email || ''}`}
            position="bottom"
        >
            <Link href={href}>
                <Avatar
                    imageUrl={ImageApi_createURL({
                        attachableId: auth.user.id,
                        attachableType: 'user/profile',
                    })}
                    label={firstLetter}
                    color={props.color ?? 'custom'}
                />
            </Link>
        </Tooltip>
    ) : (
        <Link
            href={href}
            className={cn(
                'flex text-white',
                props.full
                    ? 'items-center gap-x-3 p-3 leading-6'
                    : 'h-6 w-6 items-center justify-center text-3xl font-bold',
            )}
        >
            <div className="flex h-6 w-6 items-center justify-center text-3xl font-bold text-white">
                <Icon name="user" />
            </div>
            {props.full && <span className="font-medium">{i18n._('My')} Funkey</span>}
        </Link>
    );
};
export default AccountButton;
